<script setup lang="ts">
import { useField, useForm } from 'vee-validate'
import { ref } from 'vue'
import * as yup from 'yup'
import { isoToFaName } from '@/components/constants/iso-to-fa-name.constant'
import { useGeneralSettings } from '@/composables/useGeneralSettings'
import ErrorIcon from '/svg/error-icon.svg'

const isOpen = ref(false)

// Validation schema
const schema = yup.object({
  mobile: yup
    .string()
    .required('شماره موبایل الزامی است')
    .matches(/^09\d{9}$/, 'شماره موبایل معتبر نیست'),

  email: yup.string().required('ایمیل الزامی است').email('ایمیل معتبر نیست'),

  selfie: yup.mixed().required('تصویر سلفی الزامی است'),

  identification_document: yup.mixed().required('مدرک هویتی الزامی است'),

  first_name: yup.string().required('نام الزامی است'),

  last_name: yup.string().required('نام خانوادگی الزامی است'),

  national_code: yup
    .string()
    .required('کد ملی الزامی است')
    .matches(/^\d{10}$/, 'کد ملی باید ۱۰ رقم باشد'),

  birth_date: yup.string().required('تاریخ تولد الزامی است'),

  gender: yup.string().required('جنسیت الزامی است'),
})

// Form setup
const { handleSubmit, errors, resetForm } = useForm({
  validationSchema: schema,
  initialValues: {
    mobile: '',
    email: '',
    selfie: null,
    identification_document: null,
    first_name: '',
    last_name: '',
    national_code: '',
    birth_date: '',
    gender: 'MALE',
  },
})

// Fields
const { value: mobile } = useField('mobile')
const { value: email } = useField('email')
const { value: selfie } = useField('selfie')
const { value: identification_document } = useField('identification_document')
const { value: first_name } = useField('first_name')
const { value: last_name } = useField('last_name')
const { value: national_code } = useField('national_code')
const { value: birth_date } = useField('birth_date')
const { value: gender } = useField('gender')

const { countries } = useGeneralSettings()

// Submit
const onSubmit = handleSubmit((values) => {
  console.log('Create User:', values)
})

const closeDrawer = () => {
  isOpen.value = false
  resetForm()
}
</script>

<template>
    <div>
      <!-- TODO: disabled in 2.1.4 version -->
        <!-- <Button @click="isOpen = true" variant="outline" :isAdmin="true"
            class="px-3! rounded-lg! h-8! py-0 text-[13px] font-medium">
            <span class="flex items-center gap-2.5">
                <img src="/svg/circle-plus-primary.svg" />
                کاربر جدید
            </span>
        </Button> -->

        <OffCanvasPanel v-model="isOpen" width="650px" mobile-height="100dvh">
            <!-- Header -->
            <template #header>
                <span class="font-medium text-base text-[#0F1114] ">
                    کاربر جدید
                </span>
            </template>

            <!-- Body -->
            <form id="create-user-form" @submit.prevent="onSubmit" class="px-8 pt-2 pb-10 ">
                <!-- Grid 2 -->
                <div class="flex flex-col lg:grid grid-cols-2 gap-4 lg:gap-2 ">

                    <!-- Mobile -->
                    <div>
                        <PhoneInput :isoToFaName="isoToFaName" :countries="countries" name="mobile" appearance="admin"
                            countryFieldName="country" label="شماره موبایل" v-model="mobile" :error="errors.mobile" />
                    </div>

                    <!-- Email -->
                    <Input v-model="email" name="email" label="ایمیل" type="email" :error="errors.email"
                        appearance="admin" />

                    <!-- Selfie -->
                    <div class="mb-6 lg:mb-4">
                        <label v-if="selfie" class="block text-xs text-[#6C6C6C]">
                            تصویر سلفی
                        </label>

                        <FileUploader v-model="selfie" label="تصویر سلفی" />
                        <div v-if="errors.selfie"
                            class="flex items-center gap-1 text-[10px] font-bold text-[#ed145b] mt-1">
                            <img :src="ErrorIcon" width="14" alt="error-icon" />
                            {{ errors.selfie }}
                        </div>
                    </div>

                    <!-- Identification Document -->
                    <div class="mb-6 lg:mb-4">
                        <label v-if="identification_document" class="block  text-xs text-[#6C6C6C]">
                            مدرک هویتی
                        </label>
                        <FileUploader v-model="identification_document" label="مدرک هویتی" />
                        <div v-if="errors.identification_document"
                            class="flex items-center gap-1 text-[10px] font-bold text-[#ed145b] mt-1">
                            <img :src="ErrorIcon" width="14" alt="error-icon" />
                            {{ errors.identification_document }}
                        </div>
                    </div>

                    <!-- First Name -->
                    <Input v-model="first_name" name="first_name" label="نام" type="text" :error="errors.first_name"
                        appearance="admin" />

                    <!-- Last Name -->
                    <Input v-model="last_name" name="last_name" label="نام خانوادگی" type="text"
                        :error="errors.last_name" appearance="admin" />
                </div>

                <!-- Grid 3 -->
                <div class="flex flex-col lg:grid grid-cols-3 gap-4 mt-4 lg:mt-2">

                    <!-- National Code -->
                    <Input v-model="national_code" name="national_code" label="کد ملی" type="text"
                        :error="errors.national_code" appearance="admin" />

                    <!-- Birth Date -->
                    <AlirezaDatePicker name="birth_date" v-model="birth_date" label="تاریخ تولد"
                        placeholder="تاریخ تولد" :error="errors.birth_date" appearance="admin" />

                    <!-- Gender -->
                    <SegmentedControl name="gender" v-model="gender" class="mt-16 lg:mt-0" :options="[
                        { label: 'مرد', value: 'MALE' },
                        { label: 'زن', value: 'FEMALE' },
                    ]" :error="errors.gender" />

                </div>
            </form>

            <!-- Footer -->
            <template #footer>
                <div class="flex justify-end">
                    <Button type="submit" form="create-user-form" label="ثبت کاربر" :isAdmin="true"
                        class="w-fit h-11  px-6! " size="sm" />
                </div>
            </template>
        </OffCanvasPanel>
    </div>
</template>

<style scoped></style>
